import { Link } from '@customink/pigment-react';
import Alert from '@customink/pigment-react/lib/components/Alert';
import type { SwatchData } from '@customink/pigment-react/lib/components/ColorSwatch/ColorSwatchGroup';
import { ColorSwatchGroup } from '@customink/pigment-react/lib/components/ColorSwatch/ColorSwatchGroup';
import type {
  Product,
  ProjectItem,
  Swatch,
} from '@customink/project-service-react-components';
import { useProject } from '@customink/project-service-react-components';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { WCIBMO_NAMES } from '@/constants';
import { usePatchUpdateItem } from '@/hooks/ProjectItem';
import { useOrderQuantityType } from '@/hooks/useOrderQuantityType';
import { hasValidZipCodeAtom } from '@/hooks/useValidateZipCode';
import { pageViewTag } from '@/utils/metrics';

type MinimumColorQtyProps = {
  product?: Product;
  projectItem?: ProjectItem;
  onIncreaseQuantityClick?: () => void;
};

/**
 * The minimum order quantity is not met, but there is a no-minimum color
 * available for the selected style.
 */
export default function MinimumColorQty({
  product,
  projectItem,
  onIncreaseQuantityClick,
}: MinimumColorQtyProps) {
  const [updateProjectItem] = usePatchUpdateItem();
  const { data: project } = useProject();
  const color = product?.colors?.find(
    (color) => color.id === projectItem?.product?.colorId,
  );
  const hasValidZipCode = useAtomValue(hasValidZipCodeAtom);
  const quantityType = useOrderQuantityType();

  const updateItem = useCallback(
    (event) => {
      if (projectItem?.id) {
        const projectItemId = projectItem.id;
        updateProjectItem(projectItemId, {
          product: {
            colorId: event.value,
          },
        });
      }
    },
    [projectItem?.id, updateProjectItem],
  );

  const unusedSinglesEnabledSwatches = product?.swatches?.filter(
    (swatch) =>
      swatch.singlesEnabled &&
      !project?.items
        ?.map((item) => item.product?.colorId)
        ?.includes(swatch.colorId),
  );

  const swatchColorData: SwatchData[] = (
    unusedSinglesEnabledSwatches || []
  ).map((swatch: Swatch) => {
    const swatchData: SwatchData = {
      color: swatch.hex,
      trimRgb: swatch.trimRgb,
      value: swatch.colorId,
    };
    return swatchData;
  });

  if (!product || !color) return null;
  if (hasValidZipCode) {
    pageViewTag(
      `checkout/summary/${quantityType}/wcibmo/${WCIBMO_NAMES.RAISE_ABOVE_MIN_OR_PRODUCT}`,
    );
  }

  return (
    <>
      <Alert data-testid="raise-quantity" severity="error">
        <strong>
          The minimum order quantity for this color ({color.name}) of the{' '}
          {product.name} is {product.minimum_bulk_quantity}.
        </strong>{' '}
        Please either:
        <ul
          style={{
            fontSize: '14px',
            lineHeight: '1.5',
            marginTop: '4px',
            paddingLeft: '16px',
          }}
        >
          <li style={{ marginBottom: '4px' }}>
            <Link onClick={onIncreaseQuantityClick}>
              Increase the order quantity
            </Link>{' '}
            to {product.minimum_bulk_quantity} or more, or
          </li>
          <li>
            Try the {product.name} in one of these colors, available at any
            quantity. You can review your selection in the Design Lab.
          </li>
        </ul>
        <ColorSwatchGroup
          defaultSelected={false}
          onSwatchSelect={updateItem}
          swatchData={swatchColorData || []}
          sx={{ marginBottom: '8px' }}
        />
      </Alert>
    </>
  );
}
